import React, { useEffect } from "react";
import { Tone } from "../../model/core";
import RydHeaderDialogBox from "../widgets/RydHeaderDialogBox";
import './ContextTonality.css';
import _ from "lodash";
import { RydToneSelector } from "./RydToneSelector";
import {
    IntervalPattern,
    ScalePattern,
    Tonality
} from "../../model/scales";
import { ScalePatternSelector } from "./ScalePatternSelector";
import { ScalesService } from "../../services/ScalesService";
import {
    keybindingsGlobalResume,
    keybindingsGlobalSuspend
} from "../../keybindings/dispatcher";
import { RydToneMultiSelectKeybed } from "../rolls/PianoKeybed";


export function TonalityInputBox({
    tonality,
    setTonalityCB,
    autoClose = true
}: {
    tonality?: Tonality,
    setTonalityCB?: (tonality?: Tonality) => void,
    autoClose?: boolean
}) {

    const [collapsed, setCollapsed] = React.useState(true);
    const [center, setCenter] = React.useState<Tone | undefined>(tonality?.center);
    const [scalePattern, setScalePattern] = React.useState<ScalePattern | undefined>(tonality?.scalePattern);
    const [modeShift, setModeShift] = React.useState(tonality?.modeShift || 0);

    useEffect(() => {
        if (!setTonalityCB || !center || !scalePattern || (!modeShift && modeShift !== 0)) return;
        setTonalityCB(new Tonality(center, scalePattern, modeShift));
        if (autoClose) setCollapsed(true);
    }, [center, scalePattern, modeShift]);

    useEffect(() => {
        if (collapsed) keybindingsGlobalResume();
        else keybindingsGlobalSuspend();
    }, [collapsed]);

    const buttonCSS = (b: boolean) => (b ? ['ryd-button-option--active'] : []).concat('ryd-button-option').join(' ');

    if (collapsed) {
        return (<button onClick={() => setCollapsed(false)}>
            {center ? center.getName() : '-'} {scalePattern ? scalePattern.name : '-'} ({modeShift})
        </button>);
    }

    return (<RydHeaderDialogBox closeCB={() => { setCollapsed(true); }}>
        <div>
            <div style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'right',
                columnGap: '10px',
            }}>
                <RydToneSelector
                    centerChanged={setCenter}
                    center={center}
                    buttonCSS={buttonCSS}
                />
                <ScalePatternSelector
                    buttonCSS={buttonCSS}
                    scalePatternProvider={ScalesService.listPatterns}
                    selectedScalePatternId={scalePattern?.id}
                    setSelectedScalePatternId={(id) => {
                        const pattern = id ? ScalesService.get(id) : undefined;
                        if (!pattern) return;
                        setScalePattern(pattern);
                    }}
                />
                <div>
                    <label>Shift (mode)</label>
                    <ul className='midi-device-panel--list'>
                        {Array(scalePattern ? scalePattern.pattern.intervals.length : 0).fill(0).map((_, i) =>
                            <li
                                key={i}
                                value={i}
                                onClick={(e) => {
                                    e.preventDefault();
                                    // if (!scalePattern || !center || !setTonalityCB) return;
                                    setModeShift(i);
                                }}
                                className={buttonCSS(i === modeShift)}
                                aria-label={`Shift to mode ${i}`}>
                                {i}
                            </li>
                        )}
                    </ul>
                </div>
            </div>

            <RydToneMultiSelectKeybed
                tones={tonality ? tonality.toneIndexes.map(i => Tone.fromIndex(i)) : []}
                setSelectedTones={(tones) => { }}
                stylingSource={(n) => {
                    if (!tonality) return 'out';
                    if (n.index === tonality.center.index) return 'center';
                    return Tonality.containsTone(tonality, n) ? 'in' : 'out';
                }}
            />
        </div>
    </RydHeaderDialogBox>);
}


export function RydIntervalPatternSelector({
    patternChanged,
    intervalPattern,
    buttonCSS,
    scalePatterns
}: {
    patternChanged: (value: string | undefined, e: any) => void,
    intervalPattern?: IntervalPattern,
    buttonCSS: (b: boolean) => string,
    scalePatterns?: ScalePattern[]
}) {

    return (<div>
        <label>Interval Pattern</label>
        <ul className='midi-device-panel--list'>
            <li onClick={_.curry(patternChanged)(undefined)}>---</li>

            {scalePatterns?.map((p, i) =>
                <li
                    key={i}
                    value={p.name}
                    onClick={_.curry(patternChanged)(p.name)}
                    className={buttonCSS(p.pattern == intervalPattern)}
                    aria-label={`(${p ? p.pattern.intervals.join(', ') : ''})`}>
                    {p.name}
                </li>
            )}
        </ul>
    </div>);
}